<template>
  <div class="managers-status-page">
    <div class="page-header">
      <h1>Статусы менеджеров</h1>
      <p class="page-desc">Кто в системе и в каком статусе. Для распределения заказов учитываются только менеджеры со статусом «Онлайн».</p>
    </div>

    <div v-if="!isLogist && awayOver15Min.length" class="away-alert">
      <strong>Внимание:</strong> следующие менеджеры в статусе «Отошел» более 15 минут:
      <ul>
        <li v-for="m in awayOver15Min" :key="m.retailCrmId || m.id">{{ m.label }}</li>
      </ul>
    </div>

    <div v-if="loading" class="loading">Загрузка…</div>
    <div v-else class="table-wrap">
      <table class="managers-status-table">
        <thead>
          <tr>
            <th>Менеджер</th>
            <th>Статус</th>
            <th>В статусе с</th>
            <th v-if="!isLogist">Отошел (сейчас)</th>
            <th v-if="!isLogist">Всего «Отошел» за день</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="m in managers" :key="m.retailCrmId || m.id" :class="{ 'row-away-long': isAwayOver15(m) }">
            <td>{{ m.label }}</td>
            <td>
              <span :class="['status-badge', 'status-' + m.status]">{{ statusLabel(m.status) }}</span>
            </td>
            <td>{{ formatStatusSince(m.status_since, m.status) }}</td>
            <td v-if="!isLogist">{{ m.status === 'away' ? formatDuration(currentAwaySeconds(m)) : '—' }}</td>
            <td v-if="!isLogist">{{ formatDuration(totalAwayToday(m)) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
const API_BASE = 'https://manager.1akb.by';
const AWAY_LIMIT_MS = 15 * 60 * 1000;

export default {
  name: 'ManagersStatusPage',
  data() {
    return {
      managers: [],
      loading: true,
      tick: 0
    };
  },
  computed: {
    isLogist() {
      const u = this.$store.state.user;
      const role = (u && u.role) || '';
      const r = String(role).toLowerCase();
      return role && (r.includes('логист') || r === 'logist');
    },
    awayOver15Min() {
      const now = Date.now();
      return this.managers.filter((m) => {
        if (m.status !== 'away' || !m.status_since) return false;
        const since = new Date(m.status_since).getTime();
        return now - since >= AWAY_LIMIT_MS;
      });
    }
  },
  mounted() {
    this.fetchStatuses();
    this.refreshInterval = setInterval(this.fetchStatuses, 30 * 1000);
    this.tickInterval = setInterval(() => { this.tick = Date.now(); }, 10000);
    const ws = this.$ws;
    if (ws) {
      const handler = (e) => {
        try {
          const data = JSON.parse(e.data);
          if (data.type === 'managersStatusUpdated') this.fetchStatuses();
        } catch (_) {}
      };
      ws.addEventListener('message', handler);
      this._wsHandler = () => { ws.removeEventListener('message', handler); };
    }
  },
  beforeDestroy() {
    if (this.refreshInterval) clearInterval(this.refreshInterval);
    if (this.tickInterval) clearInterval(this.tickInterval);
    if (this._wsHandler) this._wsHandler();
  },
  methods: {
    currentAwaySeconds(m) {
      if (m.status !== 'away' || !m.status_since) return 0;
      return Math.floor((Date.now() - new Date(m.status_since).getTime()) / 1000);
    },
    totalAwayToday(m) {
      const base = m.total_away_seconds_today || 0;
      if (m.status === 'away' && m.status_since) {
        const current = Math.floor((Date.now() - new Date(m.status_since).getTime()) / 1000);
        return base - (m.away_duration_seconds || 0) + current;
      }
      return base;
    },
    statusLabel(s) {
      if (this.isLogist && s === 'away') return 'Не онлайн';
      return {
        online: 'Онлайн',
        away: 'Отошел',
        inactive: 'Не активен',
        force_offline: 'Отключён (админ)'
      }[s] || s;
    },
    formatStatusSince(iso, status) {
      if (!iso || status === 'inactive') return '—';
      const d = new Date(iso);
      return d.toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' });
    },
    formatDuration(seconds) {
      if (!seconds || seconds < 0) return '—';
      const h = Math.floor(seconds / 3600);
      const m = Math.floor((seconds % 3600) / 60);
      if (h > 0) return `${h} ч ${m} мин`;
      return `${m} мин`;
    },
    isAwayOver15(m) {
      if (m.status !== 'away' || !m.status_since) return false;
      return Date.now() - new Date(m.status_since).getTime() >= AWAY_LIMIT_MS;
    },
    async fetchStatuses() {
      this.loading = true;
      try {
        const res = await fetch(`${API_BASE}/managers-status`);
        if (res.ok) this.managers = await res.json();
      } catch (e) {
        this.managers = [];
      }
      this.loading = false;
    }
  }
};
</script>

<style scoped>
.managers-status-page {
  padding: 20px;
  max-width: 800px;
}
.page-header {
  margin-bottom: 20px;
}
.page-header h1 {
  margin: 0 0 8px 0;
  font-size: 1.5rem;
}
.page-desc {
  margin: 0;
  color: #666;
  font-size: 14px;
}
.away-alert {
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 20px;
}
.away-alert ul {
  margin: 8px 0 0 0;
  padding-left: 20px;
}
.loading {
  padding: 20px;
  color: #666;
}
.table-wrap {
  overflow-x: auto;
}
.managers-status-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}
.managers-status-table th,
.managers-status-table td {
  padding: 10px 12px;
  text-align: left;
  border-bottom: 1px solid #eee;
}
.managers-status-table th {
  background: #f5f5f5;
  font-weight: 600;
}
.row-away-long {
  background: #fff8e6;
}
.status-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
}
.status-online {
  background: #d4edda;
  color: #155724;
}
.status-away {
  background: #fff3cd;
  color: #856404;
}
.status-inactive {
  background: #e2e3e5;
  color: #383d41;
}
.status-force_offline {
  background: #f8d7da;
  color: #721c24;
}
</style>
